import * as React from "react";
import { getUser, userValidationSchema } from "../../libs/user";
import TextField, { TextFieldLabel } from "../core/TextField";
import Select from "react-select";
import Button from "../core/Button";
import { Formik, FormikProps } from "formik";
import { RailsUserContext } from "../../libs/user_context";
import {
  getAllOrgs,
  getAvailableManagersForOrg,
} from "../../libs/organization";
import { mergeWith, isNull } from "lodash";
import { getAttributes } from "../../libs/api_utils";
import UserRolesEditor from "./UserRolesEditor";
import styled from "styled-components";

const defaultFormValues: Partial<IUserAttributes> = {
  email: "",
  jobTitle: "",
  managerId: null,
  name: "",
  phoneNumber: "",
  password1: "",
  password2: "",
};

function getUserAttributes(user: IUser): Partial<IUserAttributes> {
  const attrs = getAttributes(user);
  return mergeWith(attrs, defaultFormValues, (a, b) => (isNull(a) ? b : a));
}

// given an array of user objects remove current employee  
function removeCurEmployee(users, employeeID){
  users.forEach((user, index) => {
    if (user.id == employeeID){
      users.splice(index,1);
    }
  });
  return users;
}

interface IProps {
  employee?: IUser;
  updateEmployee?: (employee: IUser) => void;
  onSubmit: (
    values: Partial<IUserAttributes>,
    formikBag: FormikProps<Partial<IUserAttributes>>
  ) => void;
  currentUser?: IUser;
  org?: IOrganization;
  disableUser?: (
    userId: number | string,
    isSubmitting: (boolean) => void
  ) => void;
}

interface IState {
  loadingManagers: boolean;
  availableManagers: IUser[];
  loadingOrgs: boolean;
  availableOrgs: INormalizedObject[];
  currentManager?: IUser;
}

class EmployeeForm extends React.Component<IProps, IState> {
  state: IState = {
    loadingManagers: true,
    availableManagers: [],
    loadingOrgs: true,
    availableOrgs: [],
  };

  async componentDidMount() {
    const { currentUser, org, employee } = this.props;

    // if the passed employee has a current manager
    if (employee && employee.managerId) {
      const currentManager = await getUser(employee.manager.id);
      this.setState({ currentManager });
    }

    if (org) {
      let availableManagers = await getAvailableManagersForOrg(org.id);
      availableManagers = removeCurEmployee(availableManagers, employee.id)
      this.setState({
        availableManagers,
        loadingManagers: false,
      });
    }

    if (currentUser.isAdmin) {
      const availableOrgs = await getAllOrgs();
      this.setState({
        availableOrgs,
        loadingOrgs: false,
      });
    }
  }

  render() {
    const { employee, onSubmit, currentUser, org, disableUser } = this.props;
    const {
      availableManagers,
      loadingManagers,
      availableOrgs,
      loadingOrgs,
      currentManager,
    } = this.state;

    const allManagers =
      currentManager && !availableManagers.find(a => a.id === currentManager.id)
        ? [...availableManagers, currentManager]
        : availableManagers;

    const managersOpts = allManagers.map(u => ({
      label: u.name,
      value: u.id,
    }));

    const orgsOpts = availableOrgs.map(o => ({
      label: o.name,
      value: o.id,
    }));

    const initialValues: Partial<IUserAttributes> = employee
      ? getUserAttributes(employee)
      : {
          ...defaultFormValues,
          organizationId: org ? Number(org.id) : undefined,
        };

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={userValidationSchema}
        render={({
          values,
          errors,
          touched,
          handleChange,
          isSubmitting,
          setFieldValue,
          handleSubmit,
          setSubmitting,
        }) => (
          <form>
            <TextField
              label={"Name"}
              value={values.name}
              type={"text"}
              name={"name"}
              onChange={handleChange}
              required={true}
              valid={!errors.name}
            />
            <TextField
              label={"Email"}
              value={values.email}
              type={"email"}
              name={"email"}
              onChange={handleChange}
              required={true}
              valid={!errors.email}
            />
            <TextField
              label={"Phone Number"}
              value={values.phoneNumber}
              type={"text"}
              name={"phoneNumber"}
              onChange={handleChange}
            />
            <TextField
              label={"Job Title"}
              value={values.jobTitle}
              type={"text"}
              name={"jobTitle"}
              onChange={handleChange}
            />
            <TextFieldLabel label={"Manager"} />
            <Select
              options={managersOpts}
              isLoading={loadingManagers}
              value={managersOpts.find(
                opt =>
                  !!values.managerId &&
                  opt.value === values.managerId.toString()
              )}
              onChange={(opt: ISelectOption) =>
                setFieldValue("managerId", opt ? opt.value : null)
              }
              placeholder={"No Manager Assigned"}
            />
            <UserRolesEditor
              roles={values.roles}
              setRoles={roles => setFieldValue("roles", roles)}
              allowAdmin={currentUser.isSuperAdmin}
            />
            {currentUser.isAdmin && (
              <>
                <TextFieldLabel label={"Organization"} />
                <Select
                  options={orgsOpts}
                  isLoading={loadingOrgs}
                  value={orgsOpts.find(
                    opt =>
                      !!values.organizationId &&
                      opt.value === values.organizationId.toString()
                  )}
                  onChange={(opt: ISelectOption) =>
                    setFieldValue("organizationId", opt ? opt.value : null)
                  }
                  clearable={false}
                />
              </>
            )}
            {currentUser.isAdmin && (
              <>
                <h3>Admin Password Reset</h3>
                <TextField
                  label={"New Password"}
                  value={values.password1}
                  type={"text"}
                  name={"password1"}
                  onChange={handleChange}
                />
                <TextField
                  label={"Confirm New Password"}
                  value={values.password2}
                  type={"text"}
                  name={"password2"}
                  onChange={handleChange}
                />                
              </>
            )}
            <p>&nbsp;</p>
            <div className="level">
              <div className="level-left">
                <Button
                  disabled={isSubmitting}
                  type={"submit"}
                  onClick={handleSubmit}
                  color={"primary"}
                >
                  {employee ? "Save" : "Invite"}
                </Button>
              </div>
              <div className="level-right">
                {employee &&
                  (currentUser.isAdmin || currentUser.isOwner) &&
                  currentUser.id !== employee.id && (
                    <Button
                      disabled={isSubmitting}
                      color={"danger"}
                      onClick={() => disableUser(employee.id, setSubmitting)}
                    >
                      Remove User
                    </Button>
                  )}
              </div>
            </div>
          </form>
        )}
      />
    );
  }
}

export default (props: IProps) => (
  <RailsUserContext.Consumer>
    {userContext => <EmployeeForm currentUser={userContext.user} {...props} />}
  </RailsUserContext.Consumer>
);



// WEBPACK FOOTER //
// ./app/javascript/components/employee/EmployeeForm.tsx